import React, { useEffect, useMemo, useState } from 'react';
import { collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { Spinner } from '../components/ui';
import { coerceDate, formatDateLabel, type MemberProfileSnapshot } from '../utils/memberEngagement';
import { BODY_MAP_ENABLED } from '../config/featureFlags';
import ConsentSettings from '../components/member/ConsentSettings';
import EmergencyDetails from '../components/member/EmergencyDetails';
import CommunicationNeeds from '../components/member/CommunicationNeeds';

interface PaymentRecord {
  id: string;
  status?: string;
  packageName?: string;
  packageId?: string;
  amount?: number | null;
  amountGbp?: number | null;
  currency?: string | null;
  paidAt?: unknown;
  createdAt?: unknown;
  cardBrand?: string | null;
  last4?: string | null;
  paymentMethodBrand?: string | null;
  paymentMethodLast4?: string | null;
}

interface EditableProfileForm {
  firstName: string;
  lastName: string;
  displayName: string;
  phone: string;
}

interface IntakeSnapshot {
  personalInfo?: {
    dateOfBirth?: string;
    sexAtBirth?: string;
    phone?: string;
  };
}

interface LegacyHealthHistory {
  q2?: string;
  q4?: string;
  q5?: { value?: number; unit?: string };
  q6?: { value?: number; unit?: string };
}

function formatMoney(payment: PaymentRecord) {
  const amount = payment.amountGbp ?? payment.amount;
  if (typeof amount !== 'number') return null;
  const currency = (payment.currency || 'GBP').toUpperCase();
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency === 'GBP' ? 'GBP' : currency,
  }).format(amount);
}

function getPaymentTimestamp(payment: PaymentRecord) {
  return coerceDate(payment.paidAt || payment.createdAt);
}

function extractPaymentMethod(payment: PaymentRecord) {
  const brand = payment.cardBrand || payment.paymentMethodBrand || null;
  const last4 = payment.last4 || payment.paymentMethodLast4 || null;
  if (!brand && !last4) return null;
  return `${brand || 'Card'}${last4 ? ` ending ${last4}` : ''}`;
}

function ProfileCard({ title, children, eyebrow }: { title: string; children: React.ReactNode; eyebrow?: string }) {
  return (
    <section className="rounded-3xl border p-6 md:p-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
      {eyebrow && (
        <p className="text-xs uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
          {eyebrow}
        </p>
      )}
      <h2 className="text-2xl font-normal mb-4" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function ProfilePage() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [profile, setProfile] = useState<(MemberProfileSnapshot & Record<string, any>) | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [intake, setIntake] = useState<IntakeSnapshot | null>(null);
  const [healthHistory, setHealthHistory] = useState<LegacyHealthHistory | null>(null);
  const [form, setForm] = useState<EditableProfileForm>({
    firstName: '',
    lastName: '',
    displayName: '',
    phone: '',
  });

  useEffect(() => {
    const load = async () => {
      if (!currentUser) return;
      try {
        const [userSnap, paymentsSnap, intakeSnap] = await Promise.all([
          getDoc(doc(db, 'users', currentUser.uid)),
          getDocs(query(collection(db, 'service_payments'), where('userId', '==', currentUser.uid))),
          getDoc(doc(db, 'patient_family_history_intakes', currentUser.uid)),
        ]);

        const userData = userSnap.exists() ? userSnap.data() : null;
        setProfile(userData as (MemberProfileSnapshot & Record<string, any>) | null);
        setHealthHistory((userData?.healthHistory || null) as LegacyHealthHistory | null);
        setIntake(intakeSnap.exists() ? (intakeSnap.data() as IntakeSnapshot) : null);
        setForm({
          firstName: String(userData?.firstName || '').trim(),
          lastName: String(userData?.lastName || '').trim(),
          displayName: String(userData?.displayName || '').trim(),
          phone: String(userData?.phone || '').trim(),
        });
        setPayments(
          paymentsSnap.docs
            .map((document) => ({ id: document.id, ...document.data() } as PaymentRecord))
            .sort((left, right) => (getPaymentTimestamp(right)?.getTime() || 0) - (getPaymentTimestamp(left)?.getTime() || 0)),
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentUser]);

  const membership = profile?.membership || null;
  const dateOfBirth = intake?.personalInfo?.dateOfBirth || healthHistory?.q4 || null;
  const sexAtBirth = intake?.personalInfo?.sexAtBirth || null;
  const genderIdentity = healthHistory?.q2 || null;
  const height = healthHistory?.q5?.value
    ? `${healthHistory.q5.value} ${healthHistory.q5.unit || ''}`.trim()
    : null;
  const weight = healthHistory?.q6?.value
    ? `${healthHistory.q6.value} ${healthHistory.q6.unit || ''}`.trim()
    : null;
  const healthGoal = String(profile?.healthGoals || profile?.goals || '').trim() || null;
  const paymentMethods = useMemo(() => {
    const seen = new Set<string>();
    return payments
      .map(extractPaymentMethod)
      .filter((value): value is string => Boolean(value))
      .filter((value) => {
        if (seen.has(value)) return false;
        seen.add(value);
        return true;
      });
  }, [payments]);

  const handleSaveProfile = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!currentUser) return;
    setSaving(true);
    setNotice(null);
    try {
      const displayName = form.displayName.trim() || [form.firstName.trim(), form.lastName.trim()].filter(Boolean).join(' ');
      await setDoc(doc(db, 'users', currentUser.uid), {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        displayName,
        phone: form.phone.trim(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setProfile((current) => ({
        ...(current || {}),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        displayName,
        phone: form.phone.trim(),
      }));
      setForm((current) => ({ ...current, displayName }));
      setNotice('Profile updated.');
    } catch (error) {
      console.error('Error saving profile:', error);
      setNotice('Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!currentUser?.email) return;
    setSendingReset(true);
    setNotice(null);
    try {
      await sendPasswordResetEmail(auth, currentUser.email);
      setNotice('Password reset email sent.');
    } catch (error) {
      console.error('Error sending reset email:', error);
      setNotice('Could not send password reset email.');
    } finally {
      setSendingReset(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <section className="rounded-3xl border p-8 md:p-10 mb-8 bg-white" style={{ borderColor: 'var(--melio-border)' }}>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--melio-sage)' }}>
              Member profile
            </p>
            <h1 className="text-3xl md:text-4xl font-normal mb-3" style={{ fontFamily: "var(--melio-display)", color: 'var(--melio-charcoal)' }}>
              Account, membership, and billing
            </h1>
            <p className="text-sm md:text-base leading-relaxed" style={{ color: 'var(--melio-text-muted)' }}>
              See your current membership access, account details, recent payments, support contacts, and security options in one place.
            </p>
          </div>
        </div>
      </section>

      {notice && (
        <section className="rounded-2xl border px-5 py-4 mb-6 bg-white" style={{ borderColor: 'var(--melio-border)', color: 'var(--melio-charcoal)' }}>
          {notice}
        </section>
      )}

      <section className="grid lg:grid-cols-[0.95fr_1.05fr] gap-6 mb-8">
        <ProfileCard title="Membership" eyebrow="Access">
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span style={{ color: 'var(--melio-text-muted)' }}>Status</span>
              <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{membership?.status || 'Not set'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span style={{ color: 'var(--melio-text-muted)' }}>Level</span>
              <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{membership?.packageName || membership?.packageId || 'Not set'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span style={{ color: 'var(--melio-text-muted)' }}>Access type</span>
              <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{membership?.accessType || 'Not set'}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span style={{ color: 'var(--melio-text-muted)' }}>Started</span>
              <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{formatDateLabel(membership?.startedAt)}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span style={{ color: 'var(--melio-text-muted)' }}>Ends</span>
              <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{formatDateLabel(membership?.endsAt)}</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span style={{ color: 'var(--melio-text-muted)' }}>Cadence</span>
              <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>Quarterly review rhythm</span>
            </div>
          </div>
          <div className="rounded-2xl p-4 mt-5" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
            <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
              What the membership covers
            </p>
            <ul className="text-sm space-y-2" style={{ color: 'var(--melio-charcoal)' }}>
              <li>Clinician-reviewed prevention plan continuity</li>
              <li>Structured reminders, due-next tracking, and open-loop follow-through</li>
              <li>Secure record for uploads, wearables, and family context</li>
              <li>Care-navigation support between consultations</li>
            </ul>
          </div>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link
              to="/enrollment?package=core_membership"
              className="inline-flex items-center justify-center px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              {membership?.status === 'active' ? 'Renew or extend membership' : 'Start your membership'}
            </Link>
            <Link
              to="/results"
              className="inline-flex items-center justify-center px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#EFEEE9', color: 'var(--melio-charcoal)' }}
            >
              Open your plan
            </Link>
            {BODY_MAP_ENABLED && (
              <Link
                to="/body-map"
                className="inline-flex items-center justify-center px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#EFEEE9', color: 'var(--melio-charcoal)' }}
              >
                Open body map
              </Link>
            )}
          </div>
        </ProfileCard>

        <ProfileCard title="Payment methods and billing" eyebrow="Billing">
          {paymentMethods.length > 0 ? (
            <div className="space-y-3 mb-5">
              {paymentMethods.map((method) => (
                <div key={method} className="rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}>
                  {method}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm mb-5" style={{ color: 'var(--melio-text-muted)' }}>
              Saved payment method details are not surfaced yet. Recent payments still appear below when available.
            </p>
          )}

          {payments.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
              No billing history recorded yet.
            </p>
          ) : (
            <div className="space-y-3">
              {payments.slice(0, 5).map((payment) => (
                <div key={payment.id} className="rounded-2xl border px-4 py-3" style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)' }}>
                  <div className="flex items-center justify-between gap-4 mb-1">
                    <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{payment.packageName || payment.packageId || 'Payment'}</span>
                    <span className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>{payment.status || 'unknown'}</span>
                  </div>
                  <div className="text-sm" style={{ color: 'var(--melio-text-muted)' }}>
                    {formatMoney(payment) || 'Amount pending'}{getPaymentTimestamp(payment) ? ` • ${formatDateLabel(getPaymentTimestamp(payment))}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ProfileCard>
      </section>

      {/* Medical Emergency §5.1 — the four things Melio must hold before any
          clinical contact. The emergency contact used to be dead code in an
          unrouted file, and the GP details were not in the member data model at
          all. */}
      <section className="mb-8">
        <ProfileCard title="If something goes wrong" eyebrow="Emergency">
          <EmergencyDetails />
        </ProfileCard>
      </section>

      {/* Consent §9.1 / Complaints §5.2 / Inclusion §8 — the Accessible
          Information Standard's identify-and-record step. */}
      <section className="mb-8">
        <ProfileCard title="How we contact you" eyebrow="Accessibility">
          <CommunicationNeeds />
        </ProfileCard>
      </section>

      {/* Consent §3.4 — withdrawal has to be as straightforward as giving it was,
          so it lives where the member manages everything else about their account
          rather than behind an email address in the privacy notice. */}
      <section className="mb-8">
        <ProfileCard title="Your consent" eyebrow="Permissions">
          <ConsentSettings />
        </ProfileCard>
      </section>

      <section className="grid lg:grid-cols-[1.05fr_0.95fr] gap-6 mb-8">
        <ProfileCard title="Profile information" eyebrow="Account">
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>First name</span>
                <input
                  value={form.firstName}
                  onChange={(event) => setForm((current) => ({ ...current, firstName: event.target.value }))}
                  className="w-full rounded-xl border px-4 py-3 text-sm"
                  style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Last name</span>
                <input
                  value={form.lastName}
                  onChange={(event) => setForm((current) => ({ ...current, lastName: event.target.value }))}
                  className="w-full rounded-xl border px-4 py-3 text-sm"
                  style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
                />
              </label>
            </div>
            <label className="block">
              <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Display name</span>
              <input
                value={form.displayName}
                onChange={(event) => setForm((current) => ({ ...current, displayName: event.target.value }))}
                className="w-full rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Phone</span>
              <input
                value={form.phone}
                onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                className="w-full rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--b-bone)', color: 'var(--melio-charcoal)' }}
              />
            </label>
            <label className="block">
              <span className="block text-sm font-medium mb-1.5" style={{ color: 'var(--melio-charcoal)' }}>Email</span>
              <input
                value={currentUser?.email || ''}
                disabled
                className="w-full rounded-xl border px-4 py-3 text-sm"
                style={{ borderColor: 'var(--melio-border)', backgroundColor: '#EFEEE9', color: 'var(--melio-text-muted)' }}
              />
            </label>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              {saving ? 'Saving...' : 'Save profile'}
            </button>
          </form>
        </ProfileCard>

        <div className="space-y-6">
          <ProfileCard title="Health details" eyebrow="Clinical context">
            <div className="space-y-3 text-sm mb-5">
              <div className="flex items-center justify-between gap-4">
                <span style={{ color: 'var(--melio-text-muted)' }}>Date of birth</span>
                <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{dateOfBirth ? formatDateLabel(dateOfBirth) : 'Not set'}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span style={{ color: 'var(--melio-text-muted)' }}>Sex at birth</span>
                <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{sexAtBirth || 'Not set'}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span style={{ color: 'var(--melio-text-muted)' }}>Gender identity</span>
                <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{genderIdentity || 'Not set'}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span style={{ color: 'var(--melio-text-muted)' }}>Height</span>
                <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{height || 'Not set'}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span style={{ color: 'var(--melio-text-muted)' }}>Weight</span>
                <span style={{ color: 'var(--melio-charcoal)', fontWeight: 600 }}>{weight || 'Not set'}</span>
              </div>
            </div>
            {healthGoal && (
              <div className="rounded-2xl p-4 mb-4" style={{ backgroundColor: 'rgba(79, 93, 24, 0.08)' }}>
                <p className="text-xs uppercase tracking-wide mb-2" style={{ color: 'var(--melio-text-muted)' }}>
                  Current goal
                </p>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--melio-charcoal)' }}>
                  {healthGoal}
                </p>
              </div>
            )}
            <Link to="/history" className="text-sm font-medium" style={{ color: 'var(--melio-sage)' }}>
              Update structured history
            </Link>
            {BODY_MAP_ENABLED && (
              <div className="mt-3">
                <Link to="/body-map" className="text-sm font-medium" style={{ color: 'var(--melio-sage)' }}>
                  Track symptoms by body region
                </Link>
              </div>
            )}
          </ProfileCard>

          <ProfileCard title="Security" eyebrow="Password">
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'var(--melio-text-muted)' }}>
              Send yourself a password reset email to change your password securely.
            </p>
            <button
              type="button"
              onClick={handleResetPassword}
              disabled={sendingReset || !currentUser?.email}
              className="inline-flex items-center justify-center px-5 py-3 rounded text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: 'var(--melio-charcoal)', color: 'var(--melio-cream)' }}
            >
              {sendingReset ? 'Sending...' : 'Send password reset email'}
            </button>
          </ProfileCard>

          <ProfileCard title="Contact Melio" eyebrow="Support">
            <div className="space-y-3 text-sm">
              <div>
                <div style={{ color: 'var(--melio-text-muted)' }}>General support</div>
                <a href="mailto:hello@meliotherapeutics.uk" style={{ color: 'var(--melio-sage)', fontWeight: 600 }}>
                  hello@meliotherapeutics.uk
                </a>
              </div>
              <div>
                <div style={{ color: 'var(--melio-text-muted)' }}>Member help</div>
                <a href="mailto:help@meliotherapeutics.uk" style={{ color: 'var(--melio-sage)', fontWeight: 600 }}>
                  help@meliotherapeutics.uk
                </a>
              </div>
              <div style={{ color: 'var(--melio-text-muted)' }}>
                Use these addresses for billing questions, booking support, account updates, and any issue that needs ops follow-up.
              </div>
            </div>
          </ProfileCard>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-16 -mt-4">
        <div
          className="rounded-2xl border px-5 py-4 flex items-center justify-between gap-4 flex-wrap"
          style={{ borderColor: 'var(--melio-border)', backgroundColor: 'var(--melio-cream-warm)' }}
        >
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em]" style={{ color: 'var(--melio-olive-deep)' }}>
              Account
            </p>
            <p className="text-sm mt-1" style={{ color: 'var(--melio-text-muted)' }}>
              Signed in as <span style={{ color: 'var(--melio-ink)', fontWeight: 600 }}>{currentUser?.email || 'your account'}</span>
            </p>
          </div>
          <Link
            to="/logout"
            className="inline-flex items-center justify-center px-4 py-2 rounded-full text-sm transition-colors"
            style={{
              border: '1px solid var(--melio-border)',
              color: 'var(--melio-text-muted)',
              backgroundColor: 'transparent',
            }}
          >
            Sign out
          </Link>
        </div>
      </section>
    </div>
  );
}
